/**
 * kb-doc-reader.tsx — Khung đọc MỘT tài liệu ở tab Tài liệu (kiểu HelpKnow): đầu trang (biểu tượng, tên sửa tại chỗ, trạng
 * "AI đã học xong", nguồn, cập nhật lúc, Xuất Word, công tắc AI dùng, Xoá) + các ĐOẠN AI đã học xếp chồng.
 *
 * - Mỗi đoạn: "Đoạn 3/12 · <tiêu đề mục> · ~N token" + nội dung; "Sửa" → ô soạn tại chỗ → Lưu = chỉ đoạn đó học lại
 *   (PATCH /knowledge/:id/chunks/:chunkId; toàn văn dựng lại từ các đoạn).
 * - "Sửa toàn văn" mở KbDocEditDialog — chia đoạn lại từ đầu.
 * - Xuất Word: tải toàn văn (.doc dạng HTML Office — Word / Google Docs mở được).
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Check, FileDown, FileSpreadsheet, FileText, Globe, Loader2, Pencil, RotateCcw, ScrollText, Trash2, X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Switch, Textarea } from '@/components/ui/misc'
import { ErrorState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { currentLocale } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { embedStatusInfo, sourceLine } from '@/lib/kb-status'
import {
  useKnowledgeChunks, useUpdateKnowledgeChunk, useUpdateKnowledgeEntry, useDeleteKnowledgeEntry, useRetryKnowledgeEntry,
  fetchKnowledgeEntry, kbLabel, type KnowledgeChunk, type KnowledgeEntry,
} from '@/hooks/use-knowledge'
import { setKnowledgeEnabled } from '@/hooks/use-ai-train-test'
import { buildWordHtml, chunkHeaderLine, docFileKind, readerStatusLabel, safeFileName, type DocFileKind, type KnowledgePerms } from './kb-lib'

const fmtTime = (s: string) => new Date(s).toLocaleString(currentLocale(), { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric' })

export function DocFileIcon({ kind, className }: { kind: DocFileKind; className?: string }) {
  const cls = cn('shrink-0', className)
  switch (kind) {
    case 'pdf': return <FileText className={cn(cls, 'text-destructive')} />
    case 'word': return <FileText className={cn(cls, 'text-primary')} />
    case 'excel': return <FileSpreadsheet className={cn(cls, 'text-success')} />
    case 'web': return <Globe className={cn(cls, 'text-primary')} />
    default: return <ScrollText className={cn(cls, 'text-muted-foreground')} />
  }
}

export function KbDocReader({ entry, perms, folderName, onEditFull, onDeleted }: {
  entry: KnowledgeEntry
  perms: KnowledgePerms
  folderName?: string | null
  /** Mở hộp "Sửa toàn văn". */
  onEditFull: () => void
  onDeleted: () => void
}) {
  const { t } = useTranslation('aiKnow')
  const qc = useQueryClient()
  const { data, isLoading, isError } = useKnowledgeChunks(entry.id)
  const rename = useUpdateKnowledgeEntry()
  const del = useDeleteKnowledgeEntry()
  const retry = useRetryKnowledgeEntry()
  // Đổi tên / xử lý lại / sửa / bật-tắt AI dùng cần knowledge.update; xoá cần knowledge.delete.
  const { canUpdate, canDelete } = perms
  const [renaming, setRenaming] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  // Trạng thái mới nhất: ưu tiên bản của API đoạn (tự làm mới khi đang học lại), lùi về dòng danh sách.
  const live = { ...entry, ...(data?.entry.id === entry.id ? data.entry : {}) }
  const st = embedStatusInfo(live)
  const chunks = data?.entry.id === entry.id ? data.chunks : []
  const src = sourceLine(entry.sourceMeta) ?? t('reader.manualSource')

  async function saveTitle() {
    const nm = (renaming ?? '').trim()
    if (!nm) { toast.error(t('reader.enterName')); return }
    if (nm === (entry.title ?? '')) { setRenaming(null); return }
    try {
      await rename.mutateAsync({ id: entry.id, data: { title: nm, changeNote: 'Đổi tên tài liệu' } }) // i18n-ignore: ghi chú phiên bản lưu vào dữ liệu
      toast.success(t('reader.renamed'))
      setRenaming(null)
    } catch (err) { toast.error(apiError(err)) }
  }

  async function toggle(on: boolean) {
    try {
      await setKnowledgeEnabled(entry.id, on)
      await qc.invalidateQueries({ queryKey: ['knowledge'] })
      toast.success(on ? t('reader.enabled') : t('reader.disabled'))
    } catch (err) { toast.error(apiError(err)) }
  }

  async function remove() {
    if (!window.confirm(t('reader.confirmDelete', { name: kbLabel(entry) }))) return
    try { await del.mutateAsync(entry.id); toast.success(t('reader.deleted')); onDeleted() } catch (err) { toast.error(apiError(err)) }
  }

  async function exportWord() {
    setExporting(true)
    try {
      // Toàn văn (đã bỏ phần chồng lấn giữa các đoạn) — không ghép trực tiếp các đoạn.
      const full = await fetchKnowledgeEntry(entry.id)
      const title = kbLabel(full)
      const html = buildWordHtml(title, [{ heading: null, content: full.content }])
      const url = URL.createObjectURL(new Blob(['﻿', html], { type: 'application/msword' }))
      const a = document.createElement('a')
      a.href = url
      a.download = safeFileName(title, 'doc')
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 1_000)
    } catch (err) { toast.error(apiError(err)) } finally { setExporting(false) }
  }

  return (
    <section className="min-w-0 rounded-lg border bg-card" aria-label={t('reader.contentLabel')}>
      {/* Đầu trang */}
      <div className="space-y-2 border-b p-4">
        <div className="flex flex-wrap items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted"><DocFileIcon kind={docFileKind(entry)} className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1">
            {renaming !== null ? (
              <form className="flex items-center gap-1" onSubmit={(e) => { e.preventDefault(); void saveTitle() }}>
                <Input autoFocus value={renaming} maxLength={200} onChange={(e) => setRenaming(e.target.value)} className="h-8" aria-label={t('reader.nameLabel')}
                  onKeyDown={(e) => { if (e.key === 'Escape') setRenaming(null) }} />
                <Button type="submit" size="icon" className="h-8 w-8" disabled={rename.isPending} aria-label={t('reader.saveName')}>
                  {rename.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                </Button>
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => setRenaming(null)} aria-label={t('reader.cancelRename')}><X className="h-4 w-4" /></Button>
              </form>
            ) : (
              <div className="flex min-w-0 items-center gap-1">
                <h2 className="truncate text-base font-semibold" title={kbLabel(entry)}>{kbLabel(entry)}</h2>
                {canUpdate && (
                  <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" title={t('common:actions.rename')} aria-label={t('reader.renameDoc')} onClick={() => setRenaming(entry.title ?? kbLabel(entry))}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            )}
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
              <Badge variant={live.status === 'archived' ? 'outline' : st.variant} className="gap-1" title={st.hint ?? st.label}>
                {st.busy && <Loader2 className="h-3 w-3 animate-spin" />}{readerStatusLabel(live, st.short)}
              </Badge>
              {st.canRetry && canUpdate && (
                <Button variant="ghost" size="sm" className="h-6 gap-1 px-1.5 text-xs" disabled={retry.isPending}
                  onClick={() => retry.mutateAsync(entry.id).then(() => toast.success(t('reader.retrying')), (err) => toast.error(apiError(err)))}>
                  <RotateCcw className="h-3 w-3" /> {t('reader.retry')}
                </Button>
              )}
              <span className="truncate" title={src}>{src}</span>
              {folderName && <span>· 📁 {folderName}</span>}
            </div>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              {t('reader.updatedAt', { time: fmtTime(live.updatedAt) })}{chunks.length ? t('reader.chunksSuffix', { n: chunks.length.toLocaleString(currentLocale()) }) : ''}
              {data?.entry.chunksEdited ? t('reader.editedByChunk') : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canUpdate && <Button variant="outline" size="sm" onClick={onEditFull}><Pencil className="h-3.5 w-3.5" /> {t('reader.editFull')}</Button>}
            <Button variant="outline" size="sm" disabled={exporting} onClick={() => void exportWord()}>
              {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} {t('reader.exportWord')}
            </Button>
            {canUpdate && (
              <label className="flex items-center gap-1.5 text-xs">
                <Switch checked={live.status !== 'archived'} disabled={live.status === 'processing'} onCheckedChange={(on) => void toggle(on)} aria-label={t('reader.aiUseLabel')} />
                {t('reader.aiUse')}
              </label>
            )}
            {canDelete && (
              <Button variant="ghost" size="icon" className="h-8 w-8" title={t('reader.deleteDoc')} aria-label={t('reader.deleteDoc')} disabled={del.isPending} onClick={() => void remove()}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            )}
          </div>
        </div>
        {st.detail && live.embedStatus !== 'no_key' && (
          <p className={cn('text-xs', st.variant === 'destructive' ? 'text-destructive' : 'text-warning')}>{st.detail}</p>
        )}
      </div>

      {/* Các đoạn AI đã học */}
      <div className="space-y-3 p-4">
        {isLoading && !chunks.length ? (
          <p className="flex items-center gap-2 py-8 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('reader.loadingChunks')}</p>
        ) : isError ? (
          <ErrorState message={t('reader.chunksError')} />
        ) : chunks.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {st.busy ? t('reader.reading') : t('reader.noContent')}
          </p>
        ) : (
          <>
            {data?.virtual && (
              <p className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
                {t('reader.virtualNote')}
              </p>
            )}
            {chunks.map((c) => (
              <ChunkBlock key={c.id ?? `v${c.index}`} entryId={entry.id} chunk={c} total={chunks.length} canEdit={canUpdate && !!c.id && live.status !== 'processing'} keywordOnly={live.embedStatus === 'no_key'} />
            ))}
          </>
        )}
      </div>
    </section>
  )
}

function ChunkBlock({ entryId, chunk, total, canEdit, keywordOnly }: {
  entryId: string; chunk: KnowledgeChunk; total: number; canEdit: boolean
  /** Hệ thống chưa có khoá AI đọc hiểu → đoạn chưa có vector là "chỉ từ khoá", không phải đang học. */
  keywordOnly?: boolean
}) {
  const { t } = useTranslation('aiKnow')
  const save = useUpdateKnowledgeChunk()
  const [draft, setDraft] = useState<string | null>(null)

  async function submit() {
    const txt = (draft ?? '').trim()
    if (!txt) { toast.error(t('reader.chunkEmpty')); return }
    if (!chunk.id) return
    try {
      await save.mutateAsync({ entryId, chunkId: chunk.id, content: txt })
      toast.success(t('reader.chunkSaved', { n: chunk.index + 1 }))
      setDraft(null)
    } catch (err) { toast.error(apiError(err)) }
  }

  return (
    <article className={cn('rounded-lg border bg-background', draft !== null && 'border-primary/60 ring-1 ring-primary/30')}>
      <header className="flex items-center gap-2 border-b bg-muted/40 px-3 py-1.5 text-[11px] text-muted-foreground">
        <span className="min-w-0 flex-1 truncate" title={chunkHeaderLine(chunk, total)}>{chunkHeaderLine(chunk, total)}</span>
        {chunk.state === 'pending' && chunk.id && (keywordOnly
          ? <Badge variant="warning" className="px-1.5 py-0 text-[10px]" title={t('reader.keywordOnlyHint')}>{t('reader.keywordOnly')}</Badge>
          : <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">{t('reader.relearning')}</Badge>)}
        {chunk.state === 'stale' && <Badge variant="outline" className="px-1.5 py-0 text-[10px]" title={t('reader.staleHint')}>{t('reader.stale')}</Badge>}
        {draft === null && canEdit && (
          <Button variant="ghost" size="sm" className="h-6 gap-1 px-1.5 text-xs" onClick={() => setDraft(chunk.content)}>
            <Pencil className="h-3 w-3" /> {t('common:actions.edit')}
          </Button>
        )}
      </header>
      {draft === null ? (
        <p className="whitespace-pre-wrap break-words px-3 py-2.5 text-sm leading-relaxed">{chunk.content}</p>
      ) : (
        <div className="space-y-2 p-2">
          <Textarea
            autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} aria-label={t('reader.editChunk', { n: chunk.index + 1 })}
            className="min-h-40 text-sm leading-relaxed" rows={Math.min(18, Math.max(6, Math.ceil(draft.length / 90)))}
          />
          <div className="flex items-center gap-2">
            <span className="flex-1 text-[11px] text-muted-foreground">{t('reader.draftInfo', { n: draft.trim().length.toLocaleString(currentLocale()) })}</span>
            <Button variant="ghost" size="sm" onClick={() => setDraft(null)} disabled={save.isPending}>{t('common:actions.cancel')}</Button>
            <Button size="sm" onClick={() => void submit()} disabled={save.isPending || draft.trim() === chunk.content.trim()}>
              {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} {t('reader.saveChunk')}
            </Button>
          </div>
        </div>
      )}
    </article>
  )
}
